/*
Copyright (C) 2023-2026 QuantumNous

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

For commercial licensing, please contact support@quantumnous.com
*/
import { useEffect, useMemo, useState } from 'react'
import { Loader2, Mail, Send } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Switch } from '@/components/ui/switch'
import {
  broadcastAnnouncementEmail,
  getAnnouncementEmailRecipients,
  type AnnouncementEmailRecipient,
  type AnnouncementEmailSource,
} from '../api'

type AnnouncementEmailDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  source: AnnouncementEmailSource
  announcementId?: number
}

const languageLabels: Record<string, string> = {
  en: 'English',
  'zh-CN': '简体中文',
  'zh-TW': '繁體中文',
  ja: '日本語',
  ko: '한국어',
  fr: 'Français',
  ru: 'Русский',
  vi: 'Tiếng Việt',
}

export function AnnouncementEmailDialog({
  open,
  onOpenChange,
  source,
  announcementId,
}: AnnouncementEmailDialogProps) {
  const { t } = useTranslation()
  const [recipients, setRecipients] = useState<AnnouncementEmailRecipient[]>([])
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set())
  const [search, setSearch] = useState('')
  const [loading, setLoading] = useState(false)
  const [sending, setSending] = useState(false)

  useEffect(() => {
    if (!open) return
    let cancelled = false
    setLoading(true)
    setSearch('')
    getAnnouncementEmailRecipients()
      .then((response) => {
        if (cancelled) return
        if (!response.success) throw new Error(response.message)
        setRecipients(response.data.recipients)
        setSelectedIds(
          new Set(response.data.recipients.map((recipient) => recipient.user_id))
        )
      })
      .catch((error) => {
        if (cancelled) return
        toast.error(
          error instanceof Error
            ? error.message
            : t('Failed to load email recipients')
        )
        onOpenChange(false)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [open, onOpenChange, t])

  const filteredRecipients = useMemo(() => {
    const keyword = search.trim().toLowerCase()
    if (!keyword) return recipients
    return recipients.filter((recipient) =>
      [recipient.username, recipient.email, recipient.language].some((value) =>
        value.toLowerCase().includes(keyword)
      )
    )
  }, [recipients, search])

  const toggleRecipient = (userId: number, checked: boolean) => {
    setSelectedIds((current) => {
      const next = new Set(current)
      if (checked) next.add(userId)
      else next.delete(userId)
      return next
    })
  }

  const handleSend = async () => {
    if (selectedIds.size === 0) {
      toast.error(t('Please select at least one recipient'))
      return
    }
    setSending(true)
    try {
      const response = await broadcastAnnouncementEmail(
        source,
        [...selectedIds],
        announcementId
      )
      if (!response.success) throw new Error(response.message)
      toast.success(
        t('Email delivery started for {{count}} users', {
          count: response.data.recipient_count,
        })
      )
      onOpenChange(false)
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : t('Failed to send announcement')
      )
    } finally {
      setSending(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='sm:max-w-4xl'>
        <DialogHeader>
          <DialogTitle className='flex items-center gap-2'>
            <Mail className='size-5' />
            {t('Announcement email recipients')}
          </DialogTitle>
          <DialogDescription>
            {t(
              "Each selected user receives a separate email. The language follows the user's preference, then the site's default language."
            )}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className='flex h-64 items-center justify-center'>
            <Loader2 className='text-muted-foreground size-6 animate-spin' />
          </div>
        ) : (
          <div className='space-y-3'>
            <div className='flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between'>
              <Input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder={t('Search username, email, or language')}
                className='sm:max-w-sm'
              />
              <div className='flex items-center gap-2'>
                <span className='text-muted-foreground text-sm'>
                  {t('{{selected}} of {{total}} selected', {
                    selected: selectedIds.size,
                    total: recipients.length,
                  })}
                </span>
                <Button
                  type='button'
                  size='sm'
                  variant='outline'
                  onClick={() =>
                    setSelectedIds(
                      new Set(recipients.map((recipient) => recipient.user_id))
                    )
                  }
                >
                  {t('Select all')}
                </Button>
                <Button
                  type='button'
                  size='sm'
                  variant='outline'
                  onClick={() => setSelectedIds(new Set())}
                >
                  {t('Clear')}
                </Button>
              </div>
            </div>

            <div className='overflow-hidden rounded-md border'>
              <div className='bg-muted/50 grid grid-cols-[3rem_minmax(8rem,1fr)_minmax(12rem,1.5fr)_8rem] gap-2 border-b px-3 py-2 text-xs font-medium'>
                <span>{t('Send')}</span>
                <span>{t('User')}</span>
                <span>{t('Email')}</span>
                <span>{t('Language')}</span>
              </div>
              <ScrollArea className='h-80'>
                {filteredRecipients.length === 0 ? (
                  <div className='text-muted-foreground flex h-32 items-center justify-center text-sm'>
                    {t('No matching recipients')}
                  </div>
                ) : (
                  filteredRecipients.map((recipient) => (
                    <div
                      key={recipient.user_id}
                      className='grid grid-cols-[3rem_minmax(8rem,1fr)_minmax(12rem,1.5fr)_8rem] items-center gap-2 border-b px-3 py-2 last:border-b-0'
                    >
                      <Switch
                        checked={selectedIds.has(recipient.user_id)}
                        onCheckedChange={(checked) =>
                          toggleRecipient(recipient.user_id, checked)
                        }
                        aria-label={t('Toggle email recipient')}
                      />
                      <span className='truncate text-sm' title={recipient.username}>
                        {recipient.username}
                      </span>
                      <span
                        className='text-muted-foreground truncate text-sm'
                        title={recipient.email}
                      >
                        {recipient.email}
                      </span>
                      <span className='text-sm'>
                        {languageLabels[recipient.language] ?? recipient.language}
                      </span>
                    </div>
                  ))
                )}
              </ScrollArea>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button
            type='button'
            variant='outline'
            onClick={() => onOpenChange(false)}
            disabled={sending}
          >
            {t('Cancel')}
          </Button>
          <Button
            type='button'
            onClick={handleSend}
            disabled={loading || sending || selectedIds.size === 0}
          >
            {sending ? (
              <Loader2 data-icon='inline-start' className='animate-spin' />
            ) : (
              <Send data-icon='inline-start' />
            )}
            {sending ? t('Starting...') : t('Send emails')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
